import React, { createContext, useState } from "react";

/**
 * The frame every VS panel renders inside: a sticky header (icon, section name,
 * status chips, actions) over the scrolling body.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY THE ACTIONS SLOT IS A CONTEXT AND NOT AN ID.
 *
 * A section's action buttons — "Add New Note", "Add Photo(s)", "View
 * Completed" — belong in the header's empty right-hand side, not at the top of
 * the content where they push the actual content down and land somewhere
 * different in every section.
 *
 * But the buttons cannot simply be lifted into `renderSectionPanel`: their
 * behaviour is the section's own ("Add New Note" toggles a draft editor that
 * lives inside ClientNotesDisplay, and flips to "Save Note"). Lifting them
 * would mean lifting that state too, and forking it.
 *
 * So the section keeps owning its buttons and PORTALS them into the header.
 * The portal target is handed down through context rather than looked up by
 * id, because during a transition TWO panels are mounted at once — two
 * headers, two slots. An id lookup would find whichever rendered first, which
 * is sometimes the panel that is on its way OUT, and the buttons would fly off
 * the screen with it. Context gives each panel its own slot by construction.
 * ─────────────────────────────────────────────────────────────────
 */

/** The header node a section should portal its action buttons into, or null. */
export const PanelActionsContext = createContext(null);

const ClientConsolePanelShell = ({
  /** { label, icon } from SECTION_META */
  meta,
  /** array of { tone, value, text } — the same set the tile abbreviates */
  statuses = [],
  /** class for the status chip, injected so this file owns no tone vocabulary */
  toneClass,
  /**
   * Give the body a DEFINITE height so a child can scroll inside it, instead
   * of growing and letting the panel scroll. Opt-in: a list section wants the
   * panel to scroll, and a definite height there would clip it.
   */
  fillBody = false,
  children,
}) => {
  /*
   * State, not a ref: a ref's `.current` assignment does not re-render, so the
   * first render would hand `null` down and the portal would never mount. The
   * callback ref sets state, the re-render supplies the node.
   */
  const [actionsSlot, setActionsSlot] = useState(null);

  return (
    <>
      <div className="scc-vs-header">
        <span className="scc-tile-icon">{meta.icon}</span>
        <h3 className="scc-vs-title">{meta.label}</h3>
        {/* The phrases live here, beside the section name, because this is
            where there is room for them. The tile carries the same set as
            bare numbers. */}
        {statuses.map((status) => (
          <span
            key={status.tone}
            className={["bdr-badge", toneClass?.[status.tone] || ""]
              .filter(Boolean)
              .join(" ")}
          >
            {status.text}
          </span>
        ))}
        <span className="scc-vs-actions" ref={setActionsSlot} />
      </div>

      <div className={`scc-vs-body${fillBody ? " scc-vs-body--fill" : ""}`}>
        <PanelActionsContext.Provider value={actionsSlot}>
          {children}
        </PanelActionsContext.Provider>
      </div>
    </>
  );
};

export default ClientConsolePanelShell;
